import { useState } from "react";
import { useAuth } from "wasp/client/auth";
import { LikeButton } from "./LikeButton";
import { CommentForm } from "./CommentForm";
import { AuthorByline } from "./AuthorByline";
import type { CommentType } from "../types";
import { UserAvatar } from "../../components/common";

interface CommentProps {
  comment: CommentType;
  onReply: (content: string, parentId: number) => Promise<void>;
  level?: number;
}

export function Comment({ comment, onReply, level = 0 }: CommentProps) {
  const { data: user } = useAuth();
  const [isReplying, setIsReplying] = useState(false);

  const handleReply = async (content: string) => {
    await onReply(content, comment.id);
    setIsReplying(false);
  };

  const isNested = level > 0;

  return (
    <div
      className={
        isNested ? "ml-4 border-l-2 border-primary/15 pl-4" : "px-5 sm:px-6"
      }
    >
      <div className="py-3">
        <div className="flex gap-2.5">
          <UserAvatar
            username={comment.user.username}
            size="sm"
            className="mt-0.5"
          />
          <div className="min-w-0 flex-1">
            {/* Meta */}
            <AuthorByline
              user={comment.user}
              date={comment.createdAt}
              className="mb-1"
            />

            {/* Content */}
            <p className="mb-2 text-sm leading-relaxed text-neutral-800 dark:text-neutral-200">
              {comment.content}
            </p>

            {/* Actions */}
            <div className="flex items-center gap-1">
              <LikeButton
                commentId={comment.id}
                isLiked={comment.isLikedByUser}
                likesCount={comment._count.likes}
                size="sm"
              />
              {user && (
                <button
                  type="button"
                  onClick={() => setIsReplying(!isReplying)}
                  className="rounded-full px-2.5 py-1 text-xs font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
                >
                  Reply
                </button>
              )}
            </div>

            {/* Reply form */}
            {isReplying && (
              <div className="mt-3">
                <CommentForm
                  onSubmit={handleReply}
                  placeholder="Write a reply..."
                  buttonText="Reply"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nested replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div>
          {comment.replies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              onReply={onReply}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
